<template>
  <div class="kt-portlet collapse-portlet-container"
       :class="{
         locked: locked_state,
         expanded: transition_state !== `hidden`, 'compact-collapse': compact,
         inline: inline,
       }">
    <div v-if="showHeading" v-b-toggle="locked_state ? `` : `collapse-${self_id}`"
         :title="tooltip"
         class="kt-portlet__head collapse-portlet-head"
         :class="{
           locked: locked_state,
         }">
      <div class="kt-portlet__head-label collapse-portlet-head-label">
        <i v-if="icon" :class="[ icon ]" />
        <h3 class="kt-portlet__head-title">
          <span>{{ title }}</span><small v-if="caption">{{ caption }}</small>
        </h3>
      </div>
      <div class="hover-capturer" />
      <div class="collapse-portlet-right-controls-container">
        <div class="collapse-portlet-right-controls" :class="{ shown: show_actions }" @click.stop>
          <slot name="portlet_actions" :toggle-expanded="(state) => toggleExpanded(state)" :toggle-lock="(state) => toggleLock(state)" />
        </div>
        <div v-if="!locked_state" class="collapse-drop-arrow">
          <i v-if="transition_state === `hidden`" class="la la-angle-down" />
          <i v-else class="la la-angle-up" />
        </div>
      </div>
    </div>
    <b-collapse :id="`collapse-${self_id}`"
                v-model="expanded_state"
                :visible="expanded"
                :accordion="accordion"
                class="collapse-portlet-body"
                @hidden="() => { transition_state = `hidden` }"
                @hide="() => transition_state = `hiding`"
                @shown="() => transition_state = `shown`"
                @show="() => transition_state = `showing`">
      <div v-if="transition_state !== `hidden`" class="collapse-portlet-body-content" :class="{ 'no-top-padding': !showHeading }">
        <slot />
      </div>
    </b-collapse>
  </div>
</template>

<script>
import { fallback } from 'Lib/type-sanity';
import { BCollapse, VBToggle } from 'bootstrap-vue';

let id_index = 0;
export default {
  components: {
    BCollapse,
  },
  directives: {
    'b-toggle': VBToggle,
  },
  props: {
    title: {
      type: String,
      required: true,
    },
    caption: {
      type: String,
      required: false,
      default: null,
    },
    showTooltip: {
      type: String,
      required: false,
      default: null,
    },
    hideTooltip: {
      type: String,
      required: false,
      default: null,
    },
    icon: {
      type: String,
      required: false,
      default: `flaticon2-gear`,
    },
    expanded: {
      type: Boolean,
      required: false,
      default: true,
    },
    accordion: {
      type: String,
      required: false,
      default: null,
    },
    compact: {
      type: Boolean,
      required: false,
      default: false,
    },
    alwaysShowActions: {
      type: Boolean,
      required: false,
      default: false,
    },
    locked: {
      type: Boolean,
      required: false,
      default: false,
    },
    lockedTooltip: {
      type: String,
      required: false,
      default: null,
    },
    inline: {
      type: Boolean,
      required: false,
      default: false,
    },
    showHeading: {
      type: Boolean,
      required: false,
      default: true,
    },
  },
  data: () => ({
    expanded_state: null,
    locked_state: null,
    transition_state: null,
    show_tooltip: ``,
    hide_tooltip: ``,
  }),
  computed: {
    tooltip() {
      if (this.locked_state === true) {
        return this.lockedTooltip;
      }
      if (this.transition_state === `shown`) {
        return this.hide_tooltip;
      }
      return this.show_tooltip;
    },
    show_actions() {
      return this.alwaysShowActions || this.transition_state === `shown` || this.transition_state === `showing`;
    },
  },
  beforeCreate() {
    this.self_id = `${id_index}-${performance.now()}`;
    id_index += 1;
  },
  beforeMount() {
    this.expanded_state = this.expanded;
    this.locked_state = this.locked;
    this.transition_state = this.expanded ? `shown` : `hidden`;
    this.show_tooltip = (() => {
      if (this.showTooltip === null) {
        return `Click to show ${this.title}`;
      }
      return this.showTooltip;
    })();
    this.hide_tooltip = (() => {
      if (this.hideTooltip === null) {
        return `Click to hide ${this.title}`;
      }
      return this.hideTooltip;
    })();
  },
  methods: {
    toggleExpanded(state) {
      this.expanded_state = fallback(state, !this.expanded_state);
    },
    toggleLock(state) {
      this.locked_state = fallback(state, !this.locked);
    },
  },
};
</script>

<style lang="scss" scoped>
@use "sass:color";
@use "sass:map";

@use "~AssetsSass/_variables" as *;

@mixin expanded {
  border-left: 1px solid map.get($ss, 'secondary');
};

.btn-sm i {
  padding: 0;
  font-size: 1.2em;
}

.collapse-portlet-container {
  border-left: 3px solid color.adjust(map.get($ss-adds, 'border-grey'), $lightness: -10%, $space: hsl);
  border-right: 1px solid map.get($ss-adds, 'border-grey');
  transition: border-left 0.1s linear;
  border-top: 1px solid map.get($ss-adds, 'border-grey');
  &.locked {
    border-left: 1px solid color.adjust(map.get($ss-adds, 'border-grey'), $lightness: -10%, $space: hsl);
  }
  &.compact-collapse {
    box-shadow: none;
    border-top-right-radius: 0;
    border-top-left-radius: 0;
    margin-bottom: 0;
  }
  &.expanded {
    border-left: 3px solid map.get($ss, 'secondary');
    transition: border-left 0.1s linear;

    &.inline {
      border-left-width: 0px;
    }

    &.locked {
      border-left-color: color.adjust(map.get($ss-adds, 'border-grey'), $lightness: -10%, $space: hsl);
    }
  }

  .collapse-portlet-head {
    position: relative;
    cursor: pointer;
    transition: background-color 0.1s linear;
    outline: none;

    &.locked {
      cursor: unset;
      transition: none;
      padding-right: 0.5em;
    }

    .collapse-portlet-head-label {
      color: map.get($ss-theme, 'hunter-grey');
      i {
        color: map.get($ss-theme, 'teal-blue');
        padding-right: .75rem;
        font-size: 1.3em;
      }
    }

    .hover-capturer {
      position: absolute;
      width: 100%;
      height: 100%;
      left: 0;
      top: 0;
      &:hover {
        background-color: color.adjust(map.get($ss, 'secondary'), $alpha: -0.9, $space: hsl);
        transition: background-color 0.1s linear;
      }
    }

    .collapse-portlet-right-controls-container {
      display: flex;
      flex-direction: row;
      align-items: center;
      .collapse-portlet-right-controls {
        z-index: 2;
        cursor: default;
        opacity: 0;
        transition: opacity 0.4s linear;
        &.hidden {
          display: none;
        }
        &.shown {
          opacity: 1;
          transition: opacity 0.4s linear;
        }
      }
      .collapse-drop-arrow {
        margin-left: 12px;
        display: flex;
        align-items: center;
        align-content: flex-end;
        text-align: center;
      }
    }
  }

  .collapse-portlet-body {
    .collapse-portlet-body-content {
      padding: 18px;

      &.no-top-padding {
        padding-top: 0;
      }
    }
  }
}
</style>
